import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Clock } from 'lucide-react';
import { Card, Badge } from '@/components/ui';
import { CompactSpecialCard } from '@/features/bars/components/CompactSpecialCard';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import type { HappyHourBarEntry } from '../utils/happyHoursPageUtils';
import { cn } from '@/lib/cn';

interface HappyHourBarCardProps {
  entry: HappyHourBarEntry;
}

export function HappyHourBarCard({ entry }: HappyHourBarCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isFavorite } = useUserPreferences();
  const barIsFavorite = isFavorite(entry.barId);

  // Group specials by type
  const drinks = entry.specials.filter((s) => s.type === 'drink');
  const food = entry.specials.filter((s) => s.type === 'food');
  const combos = entry.specials.filter((s) => s.type === 'combo');

  // Format distance for display
  const distanceText = entry.distance !== undefined
    ? `${(entry.distance / 1609).toFixed(1)} mi`
    : null;

  return (
    <Card className={cn('overflow-hidden', barIsFavorite && 'card-favorite')}>
      {/* Clickable header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-[var(--color-border)]/50 transition-colors"
      >
        {/* Bar logo */}
        {entry.barLogo ? (
          <img
            src={entry.barLogo}
            alt={entry.barName}
            className="h-12 w-12 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="h-12 w-12 rounded-lg bg-[var(--color-border)] shrink-0" />
        )}

        {/* Bar info */}
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{entry.barName}</p>
          <div className="flex items-center gap-2 mt-1">
            {distanceText && (
              <span className="text-sm text-[var(--color-muted)]">{distanceText}</span>
            )}
            <Badge variant="warning" className="flex items-center gap-1 shrink-0">
              <Clock className="h-3 w-3" />
              {entry.displayTime}
            </Badge>
          </div>
        </div>

        {/* Expand icon */}
        <ChevronDown
          className={cn(
            'h-5 w-5 text-[var(--color-muted)] transition-transform shrink-0',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-[var(--color-border)] px-4 py-3">
          {drinks.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)] mb-1">
                Drinks
              </p>
              {drinks.map((s) => (
                <CompactSpecialCard key={s.id} special={s} />
              ))}
            </div>
          )}

          {food.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)] mb-1">
                Food
              </p>
              {food.map((s) => (
                <CompactSpecialCard key={s.id} special={s} />
              ))}
            </div>
          )}

          {combos.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)] mb-1">
                Combos
              </p>
              {combos.map((s) => (
                <CompactSpecialCard key={s.id} special={s} />
              ))}
            </div>
          )}

          {/* Link to bar detail */}
          <Link
            to={`/bars/${entry.barId}`}
            className="block text-center text-sm text-accent-primary hover:underline mt-2"
          >
            View full bar details
          </Link>
        </div>
      )}
    </Card>
  );
}
